import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import sealImage from "../Images/theheads-seal.png";

const slideUp = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(-100vh);
  }
`;

const slideDown = keyframes`
  0% {
    transform: translateY(0);
  }
  100% {
    transform: translateY(100vh);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
`;

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const AnimationContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 1000;
  pointer-events: ${(props) => (props.isAnimating ? "all" : "none")};
`;

const CardHalf = styled.div`
  position: absolute;
  width: 100%;
  height: 50%;
  background: #f5e9dd;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  &.top {
    top: 0;
    animation: ${(props) => (props.isOpening ? slideUp : "none")} 2.5s
      ease-in-out forwards;
    animation-delay: 1.5s;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      width: 150px;
      height: 150px;
      background-image: url(${sealImage});
      background-size: cover;
      background-position: center;
      background-repeat: no-repeat;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &.bottom {
    bottom: 0;
    animation: ${(props) => (props.isOpening ? slideDown : "none")} 2.5s
      ease-in-out forwards;
    animation-delay: 1.5s;
  }
`;

const WelcomeText = styled.div`
  font-family: "IM Fell English", serif;
  font-size: 4vmin;
  font-weight: 400;
  text-align: center;
  opacity: ${(props) => (props.isOpening ? 0 : 1)};
  animation: ${(props) => (props.isOpening ? "none" : fadeIn)} 1s ease-in-out;
  animation-delay: 0.2s;
  animation-fill-mode: both;
  text-shadow: 0 1px 2px rgba(139, 121, 94, 0.1);
  line-height: 1.4;

  @media (max-width: 768px) {
    font-size: 5vmin;
  }
`;

const Flourish = styled.div`
  position: absolute;
  color: rgba(139, 121, 94, 0.3);
  font-family: "IM Fell English", serif;
  font-size: 2rem;

  &.top-left {
    top: 20px;
    left: 20px;
  }

  &.top-right {
    top: 20px;
    right: 20px;
  }

  &.bottom-left {
    bottom: 20px;
    left: 20px;
  }

  &.bottom-right {
    bottom: 20px;
    right: 20px;
  }
`;

const LoadingContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 10;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(27, 54, 25, 0.2);
  border-top: 3px solid #191919;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 1rem;
`;

const LoadingText = styled.p`
  font-family: "IM Fell English", serif;
  font-size: 1.2rem;
  margin: 0;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const LandingAnimation = ({ onComplete }) => {
  const [isOpening, setIsOpening] = useState(false);
  const [isAnimating, setIsAnimating] = useState(true);
  const [showLoading, setShowLoading] = useState(true);

  useEffect(() => {
    const loadingTimer = setTimeout(() => {
      setShowLoading(false);
    }, 1200);

    const timer = setTimeout(() => {
      setIsOpening(true);
    }, 1500);

    const completeTimer = setTimeout(() => {
      setIsAnimating(false);
      onComplete();
    }, 4500);

    return () => {
      clearTimeout(loadingTimer);
      clearTimeout(timer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  if (!isAnimating) return null;

  return (
    <AnimationContainer isAnimating={isAnimating}>
      <CardHalf className="top" isOpening={isOpening}></CardHalf>
      <CardHalf className="bottom" isOpening={isOpening}></CardHalf>
      <LoadingContainer show={showLoading}>
        <LoadingSpinner />
        <LoadingText></LoadingText>
      </LoadingContainer>
    </AnimationContainer>
  );
};

export default LandingAnimation;
